import React, { useMemo, useState } from 'react';
import { AlertTriangle, Building2, Download, type LucideIcon } from 'lucide-react';
import { useSocieties, useSocietyChildren } from '@/features/societies/hooks/useSocieties';
import { useInternalUsers } from '@/features/societies/hooks/useInternalUsers';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { societyApi } from '@/features/societies/api/societyApi';
import { VENDOR_TYPE_LABELS } from '@/features/societies/types/society.types';
import type { SocietyListItem } from '@/features/societies/types/society.types';
import { formatDateTimeAsUtc } from '@/features/societies/utils/dateFormat.utils';
import { DataList } from '@/Components/shared/List';
import { useListState } from '@/hooks/list';
import { FormInput } from '@/Components/shared/FormInput';
import { FormSelect } from '@/Components/shared/FormSelect';
import { SocietiesMobileCards } from './SocietiesMobileCards';
import { SocietyActionsMenu } from './SocietyActionsMenu';
import type { ColumnDef, BaseListFilters } from '@/types/list.types';

interface SocietyPageFilters extends BaseListFilters {
  nameFilter?: string;
  vendorTypeFilter?: string;
  proVersionFilter?: string;
  commercialIdFilter?: string;
  savManagerIdFilter?: string;
  supervisorIdFilter?: string;
  isActiveFilter?: boolean;
}

export interface SocietiesListPageProps {
  /** Callback when a row is clicked. If undefined, rows are not clickable. */
  onRowClick?: (society: SocietyListItem) => void;
  /** Custom header actions (buttons). If undefined, no header actions. */
  headerActions?: React.ReactNode;
  /** Label for the dynamic "wording" column (warranties/services). Default: "Garanties" */
  wordingColumnLabel?: string;
  /** Page title. Default: "Liste des sociétés" */
  title?: string;
  /** Hide the "Groupe" column */
  hideGroupColumn?: boolean;
  /** Alternative view (e.g. tree view) passed to DataList */
  alternativeView?: {
    component: React.ComponentType<any>;
    icon: LucideIcon;
    label: string;
  };
  /** Filter by specific society IDs (comma-separated). Overrides internal EXTERNAL user filtering. */
  societyIds?: string;
  /** Custom header left content (replaces title + icon + count) */
  customHeaderLeft?: React.ReactNode;
  /** Show element count next to title. Default: true */
  showCount?: boolean;
  /** Embedded mode (no outer padding/margins). Default: false */
  embedded?: boolean;
}

/**
 * Composant partagé pour la liste des sociétés
 * Utilisé par admin et outil-pro avec des props différents
 */
export const SocietiesListPage: React.FC<SocietiesListPageProps> = ({
  onRowClick,
  headerActions,
  wordingColumnLabel = 'Garanties',
  title = 'Liste des sociétés',
  hideGroupColumn = false,
  alternativeView,
  societyIds: societyIdsProp,
  customHeaderLeft,
  showCount: showCountProp = true,
  embedded = false,
}) => {
  const { currentUser, hasPermission } = useAuthStore();
  const showActionsColumn = hasPermission('society.toggle_status') || hasPermission('stripe.account');
  // Bits réservés à l'admin interne : filtres version pro + gestionnaires + export.
  const isInternal = currentUser?.type === 'INTERNAL';
  const canExport = isInternal && hasPermission('society.export');
  const [isExporting, setIsExporting] = useState(false);

  // Liste des gestionnaires (users internes) chargée au load — alimente les
  // filtres commercial / SAV / superviseur. Pas d'appel pour un user externe.
  const { data: internalUsers } = useInternalUsers({ enabled: isInternal });
  const managerOptions = useMemo(
    () => [
      { value: '', label: 'Tous' },
      // Sentinel pour filtrer les sociétés sans gestionnaire attribué (IS NULL côté back).
      { value: 'none', label: 'Aucun (non attribué)' },
      ...(internalUsers || []).map((u) => ({
        value: u.id,
        label: `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email,
      })),
    ],
    [internalUsers],
  );

  // Pour les EXTERNAL users : restreindre aux sociétés du groupe (parent + enfants)
  const { data: childrenData } = useSocietyChildren(
    currentUser?.type === 'EXTERNAL' ? currentUser.societyId || '' : ''
  );

  const societyIdsFilter = useMemo(() => {
    if (societyIdsProp) return societyIdsProp;
    if (currentUser?.type !== 'EXTERNAL' || !currentUser.societyId) return undefined;
    const ids = [currentUser.societyId, ...(childrenData || []).map(c => c.uuid)];
    return ids.join(',');
  }, [societyIdsProp, currentUser?.type, currentUser?.societyId, childrenData]);

  // Gestion de l'état de la liste
  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    activeFiltersCount,
  } = useListState<SocietyPageFilters>({
    defaultLimit: 20,
    defaultSortBy: 'lastConnectionDate',
    defaultSortDirection: 'DESC',
    persistInUrl: true,
    filterTypes: { isActiveFilter: 'boolean' },
  });

  const societyQueryFilters = {
    ...filters,
    vendorTypeFilter: filters.vendorTypeFilter ? Number(filters.vendorTypeFilter) : undefined,
    proVersionFilter: filters.proVersionFilter ? Number(filters.proVersionFilter) : undefined,
    commercialIdFilter: filters.commercialIdFilter || undefined,
    savManagerIdFilter: filters.savManagerIdFilter || undefined,
    supervisorIdFilter: filters.supervisorIdFilter || undefined,
    societyIds: societyIdsFilter,
  };

  const { data, isLoading, error } = useSocieties(societyQueryFilters);

  // Export XLSX des vendeurs — respecte les filtres affichés (hors pagination).
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      // societyQueryFilters ne porte pas la pagination/tri (gérés séparément),
      // on l'envoie tel quel : l'export sort tout le périmètre filtré.
      const blob = await societyApi.exportSocieties(societyQueryFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_vendeurs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const exportButton = canExport ? (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-text-gray-700 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors tw-font-medium disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
    >
      <Download className="tw-w-4 tw-h-4" />
      {isExporting ? 'Export…' : 'Exporter'}
    </button>
  ) : null;

  const mergedHeaderActions = (exportButton || headerActions) ? (
    <div className="tw-flex tw-items-center tw-gap-2">
      {exportButton}
      {headerActions}
    </div>
  ) : undefined;

  // Dérivation pure depuis le state (conforme CONTEXT.md)
  const societies = useMemo(() => data?.societies || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  // Définition des colonnes
  const allColumns: ColumnDef<SocietyListItem>[] = [
    ...(!hideGroupColumn ? [{
      key: 'rootParentName',
      label: 'Groupe',
      render: (society: SocietyListItem) => society.rootParentName || '—',
      sortable: true,
      sortKey: 'groupName',
    }] : []),
    {
      key: 'name',
      label: 'Nom',
      render: (society) => (
        <span className="tw-font-medium tw-inline-flex tw-items-center tw-gap-1.5">
          {society.paymentMethod === 0 && !society.permanentTransfer && (!society.billedId || society.billedId === society.uuid) && (
            <span className="tw-relative tw-group tw-inline-flex tw-cursor-help">
              <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-500 tw-flex-shrink-0" />
              <div className="tw-absolute tw-z-[9999] tw-bottom-full tw-mb-2 tw-left-1/2 -tw-translate-x-1/2 tw-w-48 tw-px-3 tw-py-2 tw-text-xs tw-leading-relaxed tw-text-white tw-bg-gray-900 tw-rounded-lg tw-shadow-lg tw-opacity-0 tw-pointer-events-none group-hover:tw-opacity-100 tw-transition-opacity tw-duration-150">
                Cette société est configurée en virement
                <div className="tw-absolute tw-top-full tw-left-1/2 -tw-translate-x-1/2 tw-border-t-[6px] tw-border-t-gray-900 tw-border-x-[6px] tw-border-x-transparent" />
              </div>
            </span>
          )}
          {society.name}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'vendorType',
      label: 'Type',
      render: (society) => VENDOR_TYPE_LABELS[society.vendorType],
      sortable: true,
    },
    ...(isInternal ? [{
      key: 'proVersion',
      label: 'API v2',
      render: (society: SocietyListItem) =>
        society.proVersion === 1 ? (
          <span className="tw-inline-flex tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded tw-bg-amber-100 tw-text-amber-800">
            Désactivée
          </span>
        ) : (
          <span className="tw-inline-flex tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-rounded tw-bg-blue-100 tw-text-blue-800">
            Activée
          </span>
        ),
      sortable: false,
    }] : []),
    {
      key: 'contractsCount',
      label: 'Contrats',
      render: (society) => (
        <span className="tw-font-medium tw-text-gray-900">
          {(society.contractsCount ?? 0).toLocaleString('fr-FR')}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'warrantiesCount',
      label: wordingColumnLabel,
      render: (society) => (
        <span className="tw-font-medium tw-text-gray-900">
          {(society.warrantiesCount ?? 0).toLocaleString('fr-FR')}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'createdAt',
      label: 'Date de création',
      render: (society) => formatDateTimeAsUtc(society.createdAt),
      sortable: true,
    },
    {
      key: 'lastConnectionDate',
      label: 'Dernière connexion',
      render: (society) => formatDateTimeAsUtc(society.lastConnectionDate),
      sortable: true,
    },
    {
      key: 'isActive',
      label: 'Statut',
      render: (society) =>
        society.isActive ? (
          <span className="tw-inline-flex tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-green-100 tw-text-green-800">
            Actif
          </span>
        ) : (
          <span className="tw-inline-flex tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-gray-100 tw-text-gray-800">
            Inactif
          </span>
        ),
      sortable: true,
    },
    ...(showActionsColumn ? [{
      key: 'actions',
      label: 'Actions',
      render: (society: SocietyListItem) => (
        <SocietyActionsMenu
          society={{ uuid: society.uuid, isActive: society.isActive }}
          variant="compact"
        />
      ),
      sortable: false,
    }] : []),
  ];

  return (
    <DataList
      // Data
      data={societies}
      total={total}
      isLoading={isLoading}
      error={error as Error}
      // Header
      title={customHeaderLeft ? undefined : title}
      titleIcon={customHeaderLeft ? undefined : Building2}
      showCount={showCountProp}
      customHeaderLeft={customHeaderLeft}
      embedded={embedded}
      headerActions={mergedHeaderActions}
      // Colonnes
      columns={allColumns}
      // Tri
      sortConfig={sortConfig}
      onSortChange={setSortConfig}
      // Filtres
      filterSearch={
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
          <FormInput
            value={filters.nameFilter || ''}
            onChange={(value) => updateFilter('nameFilter', value || undefined)}
            placeholder="Rechercher..."
            size="sm"
          />
        </div>
      }
      filters={
        <>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Type</span>
            <FormSelect
              value={filters.vendorTypeFilter || ''}
              onChange={(value) => updateFilter('vendorTypeFilter', value || undefined)}
              options={[
                { value: '', label: 'Tous les types' },
                { value: '1', label: VENDOR_TYPE_LABELS[1] },
                { value: '2', label: VENDOR_TYPE_LABELS[2] },
                { value: '3', label: VENDOR_TYPE_LABELS[3] },
              ]}
              size="sm"
            />
          </div>

          {isInternal && (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-gray-400 tw-font-normal">API publique v2</span>
              <FormSelect
                value={filters.proVersionFilter || ''}
                onChange={(value) => updateFilter('proVersionFilter', value || undefined)}
                options={[
                  { value: '', label: 'Toutes' },
                  { value: '1', label: 'Désactivée' },
                  { value: '2', label: 'Activée' },
                ]}
                size="sm"
              />
            </div>
          )}

          {isInternal && (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Commercial</span>
              <FormSelect
                value={filters.commercialIdFilter || ''}
                onChange={(value) => updateFilter('commercialIdFilter', value || undefined)}
                options={managerOptions}
                size="sm"
              />
            </div>
          )}

          {isInternal && (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Gestionnaire SAV</span>
              <FormSelect
                value={filters.savManagerIdFilter || ''}
                onChange={(value) => updateFilter('savManagerIdFilter', value || undefined)}
                options={managerOptions}
                size="sm"
              />
            </div>
          )}

          {isInternal && (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Superviseur</span>
              <FormSelect
                value={filters.supervisorIdFilter || ''}
                onChange={(value) => updateFilter('supervisorIdFilter', value || undefined)}
                options={managerOptions}
                size="sm"
              />
            </div>
          )}

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Statut</span>
            <FormSelect
              value={filters.isActiveFilter !== undefined ? String(filters.isActiveFilter) : ''}
              onChange={(value) => updateFilter('isActiveFilter', value ? value === 'true' : undefined)}
              options={[
                { value: '', label: 'Tous les statuts' },
                { value: 'true', label: 'Actif' },
                { value: 'false', label: 'Inactif' },
              ]}
              size="sm"
            />
          </div>
        </>
      }
      activeFiltersCount={activeFiltersCount}
      onResetFilters={resetFilters}
      // Pagination
      pagination={pagination}
      onPaginationChange={setPagination}
      pageSizeOptions={[10, 20, 50]}
      // Click sur ligne
      onRowClick={onRowClick}
      getRowKey={(society) => society.uuid}
      mobileCardView={SocietiesMobileCards}
      // Personnalisation
      emptyMessage={
        activeFiltersCount > 0
          ? 'Aucune société trouvée. Essayez de modifier vos filtres'
          : 'Aucune société trouvée. Créez votre première société pour commencer'
      }
      alternativeView={alternativeView}
    />
  );
};
